import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { PIPELINE_STAGES } from '@tipstrack/shared';
import {
  addApplicationNote,
  downloadResume,
  getApplication,
  updateApplicationStage,
} from '../api/endpoints.js';
import { StageBadge } from '../components/StageBadge.js';
import { errorMessage } from '../api/client.js';
import { fmtDateTime } from '../lib/format.js';

interface TimelineEntry {
  key: string;
  at: string;
  title: string;
  detail?: string;
  kind: 'stage' | 'disposition';
}

export function ApplicationDetailPage() {
  const { applicationId = '' } = useParams();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => getApplication(applicationId),
    enabled: Boolean(applicationId),
  });

  const [stageKey, setStageKey] = useState('');
  const [stageNote, setStageNote] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
    void queryClient.invalidateQueries({ queryKey: ['applications'] });
    void queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    void queryClient.invalidateQueries({ queryKey: ['pipeline-stats'] });
  }

  const stageMutation = useMutation({
    mutationFn: ({ stage, note }: { stage: string; note?: string }) =>
      updateApplicationStage(applicationId, stage, note),
    onSuccess: () => {
      invalidate();
      setStageNote('');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const noteMutation = useMutation({
    mutationFn: (body: string) => addApplicationNote(applicationId, body),
    onSuccess: () => {
      invalidate();
      setNoteBody('');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const [downloading, setDownloading] = useState(false);

  if (detailQuery.isLoading) {
    return (
      <div className="route-center">
        <div className="spinner" aria-label="Loading application" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return <div className="alert alert--error">Failed to load application.</div>;
  }

  const app = detailQuery.data;
  const currentStage = stageKey || app.stageKey;

  function onStageSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    stageMutation.mutate({ stage: currentStage, note: stageNote.trim() || undefined });
  }

  function onNoteSubmit(e: FormEvent) {
    e.preventDefault();
    const body = noteBody.trim();
    if (!body) return;
    setError(null);
    noteMutation.mutate(body);
  }

  async function onDownloadResume() {
    setDownloading(true);
    setError(null);
    try {
      await downloadResume(app.id, `${app.candidate.fullName.replace(/\s+/g, '_')}_resume`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  }

  // Merge stage events + disposition sends into one newest-first timeline.
  const timeline: TimelineEntry[] = [
    ...app.stageEvents.map((ev): TimelineEntry => {
      const from = PIPELINE_STAGES.find((s) => s.key === ev.fromStage)?.label ?? ev.fromStage;
      const to = PIPELINE_STAGES.find((s) => s.key === ev.toStage)?.label ?? ev.toStage;
      return {
        key: `stage-${ev.id}`,
        at: ev.occurredAt,
        kind: 'stage',
        title: ev.fromStage ? `Stage: ${from} → ${to}` : `Entered pipeline at ${to}`,
        detail: [ev.actorName, ev.note].filter(Boolean).join(' — ') || undefined,
      };
    }),
    ...app.outbox.map(
      (o): TimelineEntry => ({
        key: `outbox-${o.id}`,
        at: o.createdAt,
        kind: 'disposition',
        title: `Indeed disposition ${o.disposition} (${o.status})`,
        detail:
          o.status === 'failed' || o.lastError
            ? `${o.attempts} attempt(s)${o.lastError ? ` — ${o.lastError}` : ''}`
            : undefined,
      }),
    ),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <div className="breadcrumb">
            <Link to={`/jobs/${app.jobId}`}>{app.jobTitle}</Link> / application
          </div>
          <h1>{app.candidate.fullName}</h1>
          <div className="kv-row">
            <StageBadge stageKey={app.stageKey} />
            <span className="muted">Applied {fmtDateTime(app.appliedAt)}</span>
            <span className={`pill source-${app.indeedApplyId ? 'indeed' : 'manual'}`}>
              {app.indeedApplyId ? 'Indeed' : 'Manual'}
            </span>
          </div>
        </div>
      </header>

      {error && <div className="alert alert--error">{error}</div>}

      <div className="detail-grid">
        <section className="card">
          <h2 className="card__title">Candidate</h2>
          <dl className="dl">
            <dt>Email</dt>
            <dd>
              {app.candidate.email ? (
                <a href={`mailto:${app.candidate.email}`}>{app.candidate.email}</a>
              ) : (
                '—'
              )}
            </dd>
            <dt>Phone</dt>
            <dd>{app.candidate.phone ?? '—'}</dd>
            <dt>Source</dt>
            <dd>{app.candidate.source}</dd>
            <dt>Resume</dt>
            <dd>
              {app.hasResume ? (
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => void onDownloadResume()}
                  disabled={downloading}
                >
                  {downloading ? 'Downloading…' : 'Download resume'}
                </button>
              ) : (
                'None'
              )}
            </dd>
          </dl>
        </section>

        <section className="card">
          <h2 className="card__title">Stage</h2>
          <form onSubmit={onStageSubmit} className="stack">
            <label className="field">
              <span className="field__label">Pipeline stage</span>
              <select value={currentStage} onChange={(e) => setStageKey(e.target.value)}>
                {PIPELINE_STAGES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Note (optional)</span>
              <textarea
                value={stageNote}
                onChange={(e) => setStageNote(e.target.value)}
                rows={2}
                placeholder="Why is this candidate moving?"
              />
            </label>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={stageMutation.isPending || currentStage === app.stageKey}
            >
              {stageMutation.isPending ? 'Updating…' : 'Update stage'}
            </button>
          </form>
        </section>
      </div>

      <section className="card">
        <h2 className="card__title">Notes</h2>
        <form onSubmit={onNoteSubmit} className="note-form">
          <textarea
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            rows={2}
            placeholder="Add a note…"
          />
          <button
            type="submit"
            className="btn btn--primary"
            disabled={noteMutation.isPending || !noteBody.trim()}
          >
            {noteMutation.isPending ? 'Adding…' : 'Add note'}
          </button>
        </form>
        {app.notes.length === 0 ? (
          <p className="muted">No notes yet.</p>
        ) : (
          <ul className="notes">
            {app.notes.map((n) => (
              <li key={n.id} className="note">
                <div className="note__meta">
                  <strong>{n.authorName ?? 'Unknown'}</strong>
                  <span className="muted">{fmtDateTime(n.createdAt)}</span>
                </div>
                <p>{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">History</h2>
        {timeline.length === 0 ? (
          <p className="muted">No history yet.</p>
        ) : (
          <ul className="timeline">
            {timeline.map((entry) => (
              <li key={entry.key} className={`timeline__item timeline__item--${entry.kind}`}>
                <span className="timeline__dot" aria-hidden="true" />
                <div>
                  <div className="timeline__title">{entry.title}</div>
                  {entry.detail && <div className="timeline__detail">{entry.detail}</div>}
                  <div className="muted">{fmtDateTime(entry.at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
